// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { TextFieldProps } from "@insite/mobius/TextField";
import QtyField, { QtyFieldStyles } from "@stoneage/App/Components/Qty/QtyField";
import { mergeWithCss } from "@stoneage/XDL/Utilities";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: ProductQuantityStyles;
    showLabel?: boolean;
    printLabelOverride?: React.ReactNode;
    allowCantBuy?: boolean;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export interface ProductQuantityStyles {
    qtyFieldStyles?: QtyFieldStyles;
}

export const productQuantityStyles: ProductQuantityStyles = {};

const ProductQuantity = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, cantBuy, minimumOrderQty, detail },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    showLabel,
    allowCantBuy,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productQuantityStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());

    const [searchTimeoutId, setSearchTimeoutId] = useState<NodeJS.Timeout | undefined>(undefined);

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const handleBlur = (event?: React.ChangeEvent<HTMLInputElement>) => {
        if (!qtyOrdered) {
            const value = "1";
            setProductQty(value);

            if (typeof searchTimeoutId === "number") {
                clearTimeout(searchTimeoutId);
            }

            setSearchTimeoutId(
                setTimeout(() => {
                    setProductQty(value);

                    if (!onQtyOrderedChanged) {
                        logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
                        return;
                    }

                    const possibleNumber = Number(value);
                    if (!Number.isNaN(possibleNumber)) {
                        onQtyOrderedChanged(possibleNumber);
                    }
                }, 100),
            );
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);

        if (typeof searchTimeoutId === "number") {
            clearTimeout(searchTimeoutId);
        }

        setSearchTimeoutId(
            setTimeout(() => {
                setProductQty(value);

                if (!onQtyOrderedChanged) {
                    logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
                    return;
                }

                const possibleNumber = Number(value);
                if (!Number.isNaN(possibleNumber)) {
                    onQtyOrderedChanged(possibleNumber);
                }
            }, 100),
        );
    };

    if (cantBuy && !allowCantBuy) {
        return null;
    }

    return (
        <QtyField
            label={labelOverride ?? translate("QTY")}
            value={productQty}
            type="qty"
            min={minimumOrderQty || detail?.multipleSaleQty || 1}
            max={9999}
            data-test-selector="product_qtyOrdered"
            {...otherProps}
            onKeyPress={keyPressHandler}
            onChange={qtyChangeHandler}
            onBlur={handleBlur}
            showLabel={showLabel}
            extendedStyles={styles.qtyFieldStyles}
        />
    );
};

ProductQuantity.defaultProps = {
    showLabel: false,
};

export default withProductContext(connect(mapStateToProps)(ProductQuantity));
